// ─── Execution Timeline ───────────────────────────────────────────────────────
//
// Ordered log of runtime events, actions, state changes, and navigation
// for debugging, replay visualization, and audit.
//
// The timeline is bounded by MAX_ENTRIES to prevent unbounded memory growth.
// When the limit is reached, the oldest entry is evicted (ring buffer).

// ─── Types ────────────────────────────────────────────────────────────────────

export type TimelineEventType =
  | 'action'
  | 'state_change'
  | 'event'
  | 'render'
  | 'navigation'
  | 'error'
  | 'boot'
  | 'shutdown';

export interface TimelineEntry {
  id: string;
  type: TimelineEventType;
  timestamp: number;
  label: string;
  durationMs?: number;
  metadata?: Record<string, unknown>;
}

// ─── Module State ─────────────────────────────────────────────────────────────

const MAX_ENTRIES = 5_000;
const _entries: TimelineEntry[] = [];
let _counter = 0;

function _generateId(): string {
  return `tl-${Date.now().toString(36)}-${(++_counter).toString(36)}`;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Record a timeline entry.
 * Evicts the oldest entry when MAX_ENTRIES is reached.
 */
export function recordEntry(
  type: TimelineEventType,
  label: string,
  opts?: { durationMs?: number; metadata?: Record<string, unknown> },
): TimelineEntry {
  const entry: TimelineEntry = {
    id: _generateId(),
    type,
    timestamp: Date.now(),
    label,
    durationMs: opts?.durationMs,
    metadata: opts?.metadata,
  };

  _entries.push(entry);

  if (_entries.length > MAX_ENTRIES) {
    _entries.shift();
  }

  return entry;
}

/** Returns all timeline entries in chronological order (oldest first). */
export function getTimeline(): TimelineEntry[] {
  return [..._entries];
}

/** Returns the N most recent timeline entries. */
export function getRecentEntries(count: number): TimelineEntry[] {
  return _entries.slice(-count);
}

/** Returns all entries of the given type. */
export function getEntriesByType(type: TimelineEventType): TimelineEntry[] {
  return _entries.filter(e => e.type === type);
}

/** Returns entries within a time range [fromMs, toMs]. */
export function getEntriesInRange(fromMs: number, toMs: number): TimelineEntry[] {
  return _entries.filter(e => e.timestamp >= fromMs && e.timestamp <= toMs);
}

/** Clears all timeline entries. */
export function clearTimeline(): void {
  _entries.length = 0;
}

/** Returns the total number of recorded entries (including evicted ones). */
export function getTotalEntryCount(): number {
  return _counter;
}
